import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiPlus, FiTrash2, FiSave } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import { COUNTRY_OPTIONS, TEMPLATE_TAGS, appendTemplateTag } from "../utils/templateHelpers";
import "../styles/Dashboard.css";
import "../styles/Modal.css";

export default function AdvancedGeoTargetingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);
  
  const [destinations, setDestinations] = useState([]);
  
  const fetchLink = useCallback(async () => {
    try {
      const data = await api.getLink(id);
      setLink(data);
      
      let parsed = [];
      try {
        if (data.geo_destinations) {
          parsed = JSON.parse(data.geo_destinations);
        }
      } catch (e) {}
      
      // Ensure each has a unique id for React keys
      parsed = parsed.map(p => ({ ...p, id: p.id || Math.random().toString(36).substr(2, 9) }));
      setDestinations(parsed);
    } catch (err) {
      addToast("Failed to load link", "error");
      navigate("/advanced/geo-targeting");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast]);

  useEffect(() => {
    document.title = "Configure Geo-Targeting - LinkSquire";
    fetchLink();
  }, [fetchLink]);

  const handleAddDest = () => {
    setDestinations([...destinations, { id: Date.now().toString(), url: "", countryCode: "US" }]);
  };

  const handleRemoveDest = (destId) => {
    setDestinations(destinations.filter(d => d.id !== destId));
  };

  const handleUpdateDest = (destId, field, value) => {
    setDestinations(destinations.map(d => {
      if (d.id === destId) {
        return { ...d, [field]: field === "countryCode" ? value.toUpperCase() : value };
      }
      return d;
    }));
  };

  const insertTag = (destId, tag) => {
    setDestinations(destinations.map(d => {
      if (d.id === destId) {
        return { ...d, url: appendTemplateTag(d.url || "", tag) };
      }
      return d;
    }));
  };

  const handleSave = async () => {
    for (const d of destinations) {
      if (!d.url) {
        addToast("All rules must have a valid URL", "error");
        return;
      }
      if (!d.countryCode) {
        addToast("All rules must have a country selected", "error");
        return;
      }
    }

    setSaving(true);
    try {
      const cleanDestinations = destinations.map(d => ({ url: d.url, countryCode: d.countryCode }));
      await api.updateLink(id, {
        geo_targeting_enabled: true,
        geo_destinations: JSON.stringify(cleanDestinations)
      });
      addToast("Geo-Targeting configuration saved", "success");
      navigate("/advanced/geo-targeting");
    } catch (err) {
      addToast(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable Geo-Targeting for this link? Traffic will route only to the original destination URL.")) return;
    
    setSaving(true);
    try {
      await api.updateLink(id, { geo_targeting_enabled: false });
      addToast("Geo-Targeting disabled", "success");
      navigate("/advanced/geo-targeting");
    } catch (err) {
      addToast(err.message || "Failed to disable", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn-sm" onClick={() => navigate("/advanced/geo-targeting")} style={{ padding: '6px', background: 'transparent', border: '1px solid var(--border)' }}>
            <FiArrowLeft />
          </button>
          <h1 style={{ fontSize: '20px' }}>Configure Geo-Targeting</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-sm btn-sm-danger" onClick={handleDisable} disabled={saving}>
            {saving ? <span className="spinner-sm dark" /> : null} Disable Geo-Targeting
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-sm" /> Saving...</> : <><FiSave /> Save Configuration</>}
          </button>
        </div>
      </div>
      
      <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: 600 }}>Editing Link</div>
        <div style={{ fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          {link.title || link.short_code} 
          <a href={link.short_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 400, textDecoration: 'none' }}>{link.short_url}</a>
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          <strong>Default Destination:</strong> {link.original_url}
        </div>
      </div>

      <div className="links-section" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '16px', marginBottom: '4px' }}>Regional Rules</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>If a user's country matches a rule, they will be redirected there instead of the default destination.</p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {destinations.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px', border: '1px dashed var(--border)', borderRadius: '8px' }}>
              No rules added yet. Traffic will go to the default destination.
            </div>
          )}

          {destinations.map((dest, index) => (
            <div key={dest.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)' }}>
              <div style={{ width: '200px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '6px' }}>If visitor is from</label>
                <select 
                  value={dest.countryCode} 
                  onChange={(e) => handleUpdateDest(dest.id, 'countryCode', e.target.value)} 
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px', backgroundColor: 'var(--bg)' }}
                >
                  {COUNTRY_OPTIONS.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '6px' }}>Redirect them to</label>
                <input 
                  type="url" 
                  value={dest.url} 
                  onChange={(e) => handleUpdateDest(dest.id, 'url', e.target.value)} 
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px' }} 
                  placeholder="https://example.com/regional"
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {TEMPLATE_TAGS.map((item) => (
                    <button
                      key={`${dest.id}-${item.tag}`}
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '6px 10px', height: 'auto', fontSize: 12 }}
                      onClick={() => insertTag(dest.id, item.tag)}
                    >
                      {item.tag}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ paddingTop: '24px' }}>
                <button 
                  className="btn-sm btn-sm-danger" 
                  style={{ padding: '8px', height: '35px' }} 
                  onClick={() => handleRemoveDest(dest.id)}
                  data-tooltip="Remove rule"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button 
          className="btn-sm" 
          style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '6px', borderStyle: 'dashed', width: '100%', justifyContent: 'center', padding: '12px' }}
          onClick={handleAddDest}
        >
          <FiPlus /> Add regional rule
        </button>
      </div>
    </div>
  );
}
